import { fixed, trim } from '../../math/format';
import { bilateralLimits, limits } from '../../math/tolerance';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

/**
 * Print reading is knowledge rather than arithmetic, so these templates carry
 * curated case sets instead of generated numbers. They still vary: each set
 * has several cases and the options are shuffled.
 */

const SYMBOLS = [
  {
    symbol: '⌀',
    answer: 'Diameter',
    why: 'A circle with a slash means diameter. It goes in front of the number, so ⌀.500 is a half-inch hole or boss.',
    traps: [
      { label: 'Radius', trap: 'Radius is written R, with no circle. Mixing them up doubles or halves every feature.' },
      { label: 'Roundness', trap: 'Roundness is a circle on its own inside a feature control frame, no slash.' },
      { label: 'Counterbore', trap: 'Counterbore is a flat-bottomed bracket symbol, ⌴.' },
    ],
  },
  {
    symbol: 'R',
    answer: 'Radius',
    why: 'R means radius — half the width of the arc. R.250 is a quarter-inch radius, so a half-inch across if it were a full circle.',
    traps: [
      { label: 'Diameter', trap: 'Diameter is ⌀, a circle with a slash. R is half that.' },
      { label: 'Reference only', trap: 'Reference dimensions are written in brackets, like (2.000).' },
      { label: 'Rough finish', trap: 'Surface finish is a tick mark with a number, not an R.' },
    ],
  },
  {
    symbol: '⌵',
    answer: 'Countersink',
    why: 'The open V means countersink — a cone cut into the top of a hole for a flat-head screw.',
    traps: [
      { label: 'Counterbore', trap: 'Counterbore is a square-bottomed bracket, ⌴. It takes a socket head, not a flat head.' },
      { label: 'Depth', trap: 'Depth is an arrow pointing down, ↧.' },
      { label: 'Chamfer', trap: 'A chamfer is called out as a size and an angle, like .030 × 45°.' },
    ],
  },
  {
    symbol: '⌴',
    answer: 'Counterbore',
    why: 'The square bracket means counterbore — a flat-bottomed enlargement so a socket-head cap screw sits below the surface.',
    traps: [
      { label: 'Countersink', trap: 'Countersink is the open V, ⌵, and takes a tapered flat-head screw.' },
      { label: 'Spotface', trap: 'A spotface is a shallow counterbore, and is usually called out in words.' },
      { label: 'Slot', trap: 'A slot is drawn, not given a symbol like this.' },
    ],
  },
  {
    symbol: '↧',
    answer: 'Depth',
    why: 'The down arrow means depth. ⌀.250 ↧.500 is a quarter-inch hole, half an inch deep.',
    traps: [
      { label: 'Down feed', trap: 'Prints describe the part, not how you machine it.' },
      { label: 'Datum', trap: 'A datum is a letter in a box with a triangle, like ⏥A.' },
      { label: 'Below the surface', trap: 'Close in spirit, but the symbol specifically means how deep the feature goes.' },
    ],
  },
] as const;

const CONCEPTS = [
  {
    q: 'A dimension on the print is written in brackets, like (2.000). What does that mean?',
    answer: 'Reference only — not inspected',
    why: 'Brackets mean the dimension is repeated for convenience and is not a requirement. It is derived from other dimensions, and nobody measures the part against it.',
    traps: [
      { label: 'It is a basic dimension', trap: 'Basic dimensions go in a plain rectangle box, and they are exact theoretical values used with geometric tolerance.' },
      { label: 'It is the tightest tolerance', trap: 'A reference dimension has no tolerance at all, because it is not checked.' },
      { label: 'It is in millimetres', trap: 'Units are stated in the title block, not by bracketing.' },
    ],
  },
  {
    q: 'A dimension sits in a plain rectangular box. What is it?',
    answer: 'A basic dimension — exact, toleranced by a feature control frame',
    why: 'A boxed dimension is theoretically exact. Its tolerance does not come from the title block; it comes from the geometric tolerance that references it.',
    traps: [
      { label: 'Reference only', trap: 'Reference dimensions go in round brackets and are not checked at all.' },
      { label: 'It uses the title-block tolerance', trap: 'That is the one thing a basic dimension does not do — its control comes from the feature control frame.' },
      { label: 'It is a maximum limit', trap: 'Limits are written as two numbers or with a ± callout.' },
    ],
  },
  {
    q: 'The title block says ".XX = ±.01, .XXX = ±.005". A dimension reads 1.250. What is the tolerance?',
    answer: '±.005',
    why: 'Count the decimal places on the dimension itself. 1.250 has three, so it picks up the three-place tolerance — ±.005, giving 1.245" to 1.255".',
    traps: [
      { label: '±.01', trap: 'That is the two-place tolerance. 1.250 is written to three places, and the trailing zero counts.' },
      { label: '±.0005', trap: 'There is no four-place row in this title block.' },
      { label: 'No tolerance, it is exact', trap: 'Nothing on a print is exact except a basic dimension in a box. Everything else takes the title-block tolerance unless it states its own.' },
    ],
  },
  {
    q: 'What is a datum on a print?',
    answer: 'The surface everything else is measured from',
    why: 'A datum is the reference the part is set up and inspected against. Get the datum wrong and every measurement after it is wrong too, even if your arithmetic is perfect.',
    traps: [
      { label: 'The tightest tolerance on the part', trap: 'A datum is a reference, not a tolerance. It can be a rough surface.' },
      { label: 'The first operation you machine', trap: 'Often true in practice, but a datum is about how the part is measured, not the order of operations.' },
      { label: 'The centre of the part', trap: 'A datum can be a centre, but it is usually a face or an axis called out by letter.' },
    ],
  },
  {
    q: 'A hole is called out as "4× ⌀.250". What does the 4× mean?',
    answer: 'There are four of these holes',
    why: 'The multiplier says how many identical features share that one callout. You will not find the other three dimensioned anywhere else on the print.',
    traps: [
      { label: 'The hole is four times deeper than wide', trap: 'Depth gets its own symbol, ↧.' },
      { label: 'Drill it in four passes', trap: 'A print describes the finished part, never the method.' },
      { label: 'The tolerance is four times looser', trap: 'The multiplier has nothing to do with tolerance.' },
    ],
  },
] as const;

export const PRINT_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'print.symbol',
    topicId: 'print',
    skill: 'Print symbols',
    gen: (rng) => {
      const c = rng.pick(SYMBOLS);
      return {
        kind: 'mc',
        prompt: `What does ${c.symbol} mean on a print?`,
        why: c.why,
        steps: [
          { kind: 'note', text: `The symbol ${c.symbol} appears in front of the dimension it applies to.` },
          { kind: 'result', text: c.why },
        ],
        ...mcOptions(rng, c.answer, [...c.traps]),
      };
    },
  },
  {
    id: 'print.concept',
    topicId: 'print',
    skill: 'How a drawing communicates',
    gen: (rng) => {
      const c = rng.pick(CONCEPTS);
      return {
        kind: 'mc',
        prompt: c.q,
        why: c.why,
        // Deliberately not echoing the prompt as a setup step: several of
        // these questions quote a title block or a callout that contains the
        // answer, so restating them pre-answer hands it straight over.
        steps: [
          {
            kind: 'note',
            text: 'Go by what the drawing convention actually means, not by what looks reasonable. Print reading is a language, not a calculation.',
          },
          { kind: 'result', text: c.why },
        ],
        ...mcOptions(rng, c.answer, [...c.traps]),
      };
    },
  },
  {
    id: 'print.titleBlock',
    topicId: 'print',
    skill: 'Applying the title-block tolerance',
    gen: (rng) => {
      // Decimal places on the dimension decide which title-block row applies.
      const places = rng.pick([2, 3]);
      const tol = places === 2 ? 0.01 : 0.005;
      const nominal =
        places === 2 ? rng.step(0.5, 4, 0.05) : rng.step(0.5, 4, 0.125);
      const solved = bilateralLimits(nominal, tol);
      const other = places === 2 ? 0.005 : 0.01;
      const otherBand = bilateralLimits(nominal, other).value;

      const range = (min: number, max: number): string =>
        `${fixed(min, 4)}" to ${fixed(max, 4)}"`;

      return {
        kind: 'mc',
        prompt: `Title block: .XX = ±.01, .XXX = ±.005. The print says ${nominal.toFixed(places)}". What are the limits?`,
        formula: 'max = nominal + upper · min = nominal − lower',
        why: `${nominal.toFixed(places)}" is written to ${places} places, so it takes ±${trim(tol, 3).replace(/^0/, '')}. That gives ${range(solved.value.min, solved.value.max)}.`,
        steps: [
          {
            kind: 'note',
            text: 'Count the decimal places on the dimension, then find that row in the title block. Trailing zeros count.',
          },
          ...solved.steps,
        ],
        ...mcOptions(rng, range(solved.value.min, solved.value.max), [
          {
            label: range(otherBand.min, otherBand.max),
            trap: `That is the ±${trim(other, 3).replace(/^0/, '')} row. ${nominal.toFixed(places)}" has ${places} decimal places, not ${places === 2 ? 3 : 2}.`,
          },
          {
            label: range(nominal, nominal + tol * 2),
            trap: 'You put the whole tolerance on the top. ± splits it evenly either side of the nominal.',
          },
          {
            label: range(nominal - tol * 2, nominal + tol * 2),
            trap: 'That band is twice as wide as it should be. ±.01 means .01 each way, not .02.',
          },
        ]),
      };
    },
  },
  {
    id: 'print.stackUp',
    topicId: 'print',
    skill: 'Adding tolerances along a chain',
    gen: (rng) => {
      const a = rng.step(0.5, 2, 0.125);
      const b = rng.step(0.5, 2, 0.125);
      const tolA = rng.pick([0.002, 0.005, 0.01]);
      const tolB = rng.pick([0.002, 0.005, 0.01]);
      const total = a + b;
      const totalTol = tolA + tolB;
      const solved = limits(total, totalTol, totalTol);

      return {
        kind: 'mc',
        prompt: `Two features in a row: ${fixed(a, 3)}" ±${trim(tolA, 3).replace(/^0/, '')} and ${fixed(b, 3)}" ±${trim(tolB, 3).replace(/^0/, '')}. What is the overall length and tolerance?`,
        formula: 'Dimensions add. Tolerances add too.',
        why: `${trim(a, 3)} + ${trim(b, 3)} = ${fixed(total, 3)}", and the tolerances stack: ${trim(tolA, 3)} + ${trim(tolB, 3)} = ${trim(totalTol, 3)}". This is why chained dimensions get sloppy fast.`,
        steps: [
          { kind: 'formula', text: 'Dimensions add. Tolerances add too — they never cancel.' },
          { kind: 'substitute', text: `${trim(a, 3)} + ${trim(b, 3)} for the size, ${trim(tolA, 3)} + ${trim(tolB, 3)} for the tolerance` },
          { kind: 'compute', text: `Size: ${fixed(total, 4)}"` },
          { kind: 'compute', text: `Tolerance: ±${fixed(totalTol, 4)}"` },
          { kind: 'result', text: `${fixed(total, 3)}" ±${trim(totalTol, 3)}", so ${fixed(solved.value.min, 4)}" to ${fixed(solved.value.max, 4)}"` },
          {
            kind: 'note',
            text: 'Every link you add to a chain loosens the far end. Dimensioning from one datum instead avoids the stack-up entirely.',
          },
        ],
        ...mcOptions(rng, `${fixed(total, 3)}" ±${trim(totalTol, 3)}"`, [
          {
            label: `${fixed(total, 3)}" ±${trim(Math.max(tolA, tolB), 3)}"`,
            trap: 'You took the bigger of the two tolerances. Tolerances in a chain add together — they do not just take the worst one.',
          },
          {
            label: `${fixed(total, 3)}" ±${trim(Math.abs(tolA - tolB) || 0.001, 3)}"`,
            trap: 'You subtracted them. Errors accumulate along a chain, they never cancel out.',
          },
          {
            label: `${fixed(total, 3)}" ±${trim(totalTol / 2, 4)}"`,
            trap: 'You halved the stack. Each feature can be out by its own full tolerance, and both can be out the same way.',
          },
        ]),
      };
    },
  },
];
